<template>
  <div class="weight-panel">
    <div class="wp-head">
      <span class="wp-title">能力域权重</span>
      <span class="wp-desc">
        {{ editable ? '按项目侧重点调整，自动归一化到 100%' : '企业级采用优序图法全局权重，不可调整' }}
      </span>
      <button v-if="editable" type="button" class="wp-reset" @click="reset">恢复默认</button>
    </div>

    <ul class="wp-list">
      <li v-for="d in domains" :key="d.id" class="wp-item">
        <span class="wp-name">
          {{ d.name }}
          <em v-if="d.critical">关键</em>
        </span>
        <el-slider
          :model-value="raw[d.id]"
          :min="0"
          :max="100"
          :step="1"
          :disabled="!editable"
          :show-tooltip="false"
          class="wp-slider"
          @update:model-value="(v) => setRaw(d.id, v)"
        />
        <span class="wp-val">{{ pct(normalized[d.id]) }}</span>
        <span class="wp-def">默认 {{ pct(d.weight) }}</span>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { DEFAULT_WEIGHTS } from '../data/index.js'
import { normalizeWeights } from '../utils/scoring.js'

const props = defineProps({
  domains: { type: Array, required: true },
  weights: { type: Object, required: true },
  // 仅项目级允许调整；企业级采用文档给定的优序图法全局权重
  editable: { type: Boolean, default: false }
})

const emit = defineEmits(['update:weights'])

// 滑杆用 0-100 的整数表示相对重要性，再归一化为实际权重
const raw = ref({})
const normalized = ref({ ...props.weights })

function syncFromWeights (weights) {
  const max = Math.max(...Object.values(weights), 0.0001)
  const next = {}
  for (const d of props.domains) {
    next[d.id] = Math.round(((weights[d.id] || 0) / max) * 100)
  }
  raw.value = next
  normalized.value = { ...weights }
}

watch(() => props.weights, syncFromWeights, { immediate: true, deep: true })

function setRaw (id, v) {
  const next = { ...raw.value, [id]: Number(v) }
  raw.value = next
  const asWeights = {}
  for (const d of props.domains) asWeights[d.id] = next[d.id] / 100
  normalized.value = normalizeWeights(asWeights)
  emit('update:weights', { ...normalized.value })
}

function reset () {
  syncFromWeights({ ...DEFAULT_WEIGHTS })
  emit('update:weights', { ...DEFAULT_WEIGHTS })
}

function pct (v) {
  return `${((v || 0) * 100).toFixed(1)}%`
}
</script>

<style lang="scss" scoped>
.weight-panel {
  padding: 18px 20px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
}

.wp-head {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 10px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.wp-title {
  font-size: 13.5px;
  font-weight: 600;
  color: #1d1d1f;
}

.wp-desc {
  flex: 1;
  font-size: 12px;
  color: #86868b;
}

.wp-reset {
  padding: 4px 12px;
  font-size: 12px;
  color: #0071e3;
  background: rgba(0, 113, 227, 0.08);
  border: 0;
  border-radius: 999px;
  cursor: pointer;

  &:hover {
    background: rgba(0, 113, 227, 0.16);
  }
}

.wp-list {
  margin: 6px 0 0;
  padding: 0;
  list-style: none;
}

.wp-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 9px 0;
}

.wp-name {
  flex: 0 0 168px;
  font-size: 13px;
  color: #3a3a3c;

  em {
    margin-left: 6px;
    padding: 1px 6px;
    font-style: normal;
    font-size: 11px;
    color: #d1681f;
    background: rgba(209, 104, 31, 0.10);
    border-radius: 4px;
  }
}

.wp-slider {
  flex: 1;
  min-width: 0;
}

.wp-val {
  flex: 0 0 56px;
  font-size: 13px;
  font-weight: 600;
  color: #0071e3;
  text-align: right;
}

.wp-def {
  flex: 0 0 96px;
  font-size: 11.5px;
  color: #a1a1a6;
  text-align: right;
}

@media (max-width: 860px) {
  .wp-item {
    flex-wrap: wrap;
    gap: 8px;
  }

  .wp-name {
    flex: 1 1 100%;
  }

  .wp-slider {
    flex: 1 1 100%;
  }

  .wp-val,
  .wp-def {
    flex: 0 0 auto;
  }
}
</style>
